/**
 * Realtime Unilevel per-product config, shared by the plan (`@mlm/compplans`
 * realtime-unilevel.ts) and both admin editors (the product's Realtime
 * Unilevel tab and the plan page), so the three agree on what a cell means.
 *
 * A product pays a fixed amount per level (`level_amounts`, index 0 = the
 * buyer's own cashback, index k = the Level-k sponsor upline). The optional
 * `membership_matrix` varies those amounts by membership: Level 0 by the
 * BUYER's membership (the buyer is the earner there), Levels 1+ by the
 * EARNER's (row) and the BUYER's (column) membership. Keys are membership
 * CODES. A blank cell (missing, null or '') falls back to the level's default
 * amount; an explicit 0 pays nothing for that combination.
 */

export type RtuAmount = number | string;

export type RealtimeUnilevelMembershipMatrix = {
  enabled: boolean;
  /** Level 0: buyer membership code → cashback amount. */
  cashback?: Record<string, RtuAmount | null>;
  /** Levels 1+: level (as a string) → earner code → buyer code → amount. */
  levels?: Record<string, Record<string, Record<string, RtuAmount | null>>>;
};

export type RealtimeUnilevelProductConfig = {
  product_id: string;
  level_amounts: RtuAmount[];
  membership_matrix?: RealtimeUnilevelMembershipMatrix;
};

function isBlank(v: RtuAmount | null | undefined): v is null | undefined | '' {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}

/**
 * The amount a product pays at `level` for this earner/buyer pair, or
 * `undefined` when the product pays nothing at that level (past its last
 * level). `earnerCode` is ignored at Level 0. Membership codes may be null
 * (a slot with no membership), which always reads the default.
 */
export function rtuAmountFor(
  product: Pick<RealtimeUnilevelProductConfig, 'level_amounts' | 'membership_matrix'>,
  level: number,
  earnerCode: string | null,
  buyerCode: string | null,
): RtuAmount | undefined {
  const fallback = product.level_amounts[level];
  if (fallback === undefined) return undefined;
  const m = product.membership_matrix;
  if (!m?.enabled || !buyerCode) return fallback;
  const cell =
    level === 0
      ? m.cashback?.[buyerCode]
      : earnerCode
        ? m.levels?.[String(level)]?.[earnerCode]?.[buyerCode]
        : undefined;
  return isBlank(cell) ? fallback : cell;
}

/** Whether any line of this product's matrix could pay differently from the defaults. */
export function rtuMatrixInUse(product: Pick<RealtimeUnilevelProductConfig, 'membership_matrix'>) {
  return product.membership_matrix?.enabled === true;
}

/** Number of filled (non-blank) cells at a level, for the editor's level tabs. */
export function rtuOverrideCount(
  matrix: RealtimeUnilevelMembershipMatrix | undefined,
  level: number,
): number {
  if (!matrix) return 0;
  if (level === 0) {
    return Object.values(matrix.cashback ?? {}).filter((v) => !isBlank(v)).length;
  }
  let n = 0;
  for (const row of Object.values(matrix.levels?.[String(level)] ?? {})) {
    n += Object.values(row).filter((v) => !isBlank(v)).length;
  }
  return n;
}

/**
 * Remove level `level` (≥ 1) from a product: drops its default amount AND its
 * matrix grid, and shifts every higher level's grid down one so each grid
 * stays with the amount it was written for. Level 0 (cashback) can't be removed.
 */
export function rtuRemoveLevel<P extends RealtimeUnilevelProductConfig>(
  product: P,
  level: number,
): P {
  const level_amounts = product.level_amounts.filter((_, i) => i !== level);
  const m = product.membership_matrix;
  if (!m?.levels || level < 1) return { ...product, level_amounts };
  const levels: NonNullable<RealtimeUnilevelMembershipMatrix['levels']> = {};
  for (const [key, grid] of Object.entries(m.levels)) {
    const k = Number(key);
    if (!Number.isInteger(k) || k === level) continue;
    levels[String(k > level ? k - 1 : k)] = grid;
  }
  return { ...product, level_amounts, membership_matrix: { ...m, levels } };
}
